import { useMemo } from "react";
import { useParams } from "react-router";
import _ from "lodash";

import { AIIcon } from "icons";
import { colors } from "theme/palette";

import NoPrint from "components/NoPrint";
import Stack from "components/Stack";
import Text from "components/Text";

import { IPackageDetailsField } from "entities/Checklist/sdk";
import InstructionsDialogButton from "entities/Field/components/InstructionsDialogButton";
import LinkedFieldsDrawerButton from "entities/Field/components/LinkedFieldsDrawerButton";
import { FieldType } from "entities/Field/sdk";

export interface IFieldTitle {
  field: IPackageDetailsField;
  onUpdate: () => void;
  externalToken?: string;
  disabled?: boolean;
}

const FieldTitle = ({
  field,
  onUpdate,
  externalToken,
  disabled = false,
}: IFieldTitle) => {
  const params = useParams<{ dealId: string }>();
  const dealId = Number(params.dealId);

  const showLinkedFieldsDrawerButton = useMemo(
    () => field.is_linked && !_.isNil(dealId) && _.isUndefined(externalToken),
    [field, dealId, externalToken]
  );

  const showAIIcon = useMemo(
    () =>
      field.field_type === FieldType.FILE &&
      field.values.some((item) => !_.isEmpty(item.file?.openai_files)),
    [field]
  );

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      marginRight={4}
      flexBasis="400px"
      sx={{
        maxWidth: "60%",
        flexGrow: 1,
      }}
    >
      <Text
        variant="text2"
        sx={{
          fontWeight: 500,
          opacity: field.n_a_status || disabled ? 0.5 : 1,
        }}
        data-testid="field-name"
      >
        {field.name}
        <NoPrint
          sx={{
            display: "inline-flex",
            marginLeft: 0.5,
            flexDirection: "row",
            "& > * + *": {
              marginLeft: 0.5,
            },
          }}
        >
          <InstructionsDialogButton
            internalInstructions={field?.internal_instructions}
            externalInstructions={field?.external_instructions}
          />
          {showLinkedFieldsDrawerButton && (
            <LinkedFieldsDrawerButton
              isOverridden={field?.is_overridden}
              dealId={dealId}
              packageId={field.company_package_id}
              fieldId={field.company_custom_field_id}
              onUpdate={onUpdate}
            />
          )}
          {showAIIcon && <AIIcon style={{ color: colors.blue100 }} />}
        </NoPrint>
      </Text>
    </Stack>
  );
};

export default FieldTitle;
